// Blocking (brief 012, App Store Guideline 1.2). Who I've blocked lives in
// users/{uid}/blocked/{otherUid}, readable only by me, so nobody learns who blocked
// them. The rules refuse messages, requests, notifications and shares from anyone
// in the recipient's list; the app also hides blocked people everywhere.
import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, setDoc, deleteDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { confirmSheet, toast } from './components/feedback';

const blockedCol = uid => collection(db, 'users', uid, 'blocked');

// { [otherUid]: name } for everyone I've blocked, kept live.
export const useBlocked = (uid) => {
  const [blocked, setBlocked] = useState({});
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(blockedCol(uid),
      snap => setBlocked(Object.fromEntries(snap.docs.map(d => [d.id, d.data().name || 'Study partner']))),
      e => console.warn('blocked list failed', e));
  }, [uid]);
  return blocked;
};

// Drop items whose other person is blocked. `who` picks that person's uid from an item.
export const withoutBlocked = (items, blocked, who) =>
  items.filter(item => !(who(item) in blocked));

// Test accounts used by the e2e suite never show up as matches for real users
// (they still see each other, which the e2e tests rely on).
export const isTestAccount = profile => /^E2E Tester/i.test(profile?.name || '');

// Demo accounts (brief 034) are for App Store screenshots and Apple's reviewer:
// `demo: true`, set only in the Firestore console. Real people never see them, and a
// demo account sees only other demo accounts. E2E test accounts see each other and
// everyone else; real people don't see them.
export const isDemoAccount = profile => profile?.demo === true;
export const canSee = (me, other) => {
  if (isDemoAccount(me)) return isDemoAccount(other);
  if (isDemoAccount(other)) return false;
  return isTestAccount(me) || !isTestAccount(other);
};

export const blockUser = (uid, otherUid, name) =>
  setDoc(doc(blockedCol(uid), otherUid), { name: name || 'Study partner', createdAt: serverTimestamp() });

export const unblockUser = (uid, otherUid) => deleteDoc(doc(blockedCol(uid), otherUid));

// On account deletion.
export const deleteBlockList = async (uid) => {
  const snap = await getDocs(blockedCol(uid));
  await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
};

// Ask, then block. Resolves true once blocked.
export const confirmBlock = async (uid, other) => {
  const yes = await confirmSheet({
    title: `Block ${other.name}?`,
    message: `${other.name} won't be able to message you, request sessions or share documents with you, and you won't see them in MedXMeet. They aren't told. You can unblock them in Settings.`,
    confirmLabel: 'Block',
    cancelLabel: 'Cancel',
    danger: true,
  });
  if (!yes) return false;
  try {
    await blockUser(uid, other.uid, other.name);
    toast(`${other.name} is blocked.`);
    return true;
  } catch (e) {
    console.error(e);
    toast('Could not block. Please try again.', { type: 'error' });
    return false;
  }
};
